import { useState } from 'react';
import { Volume2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { speak } from '../services/speech.js';
import { useProfileStore } from '../state/profile-store.js';
import { effectiveAudio, useAudioSettingsStore } from '../state/audio-settings-store.js';

export type SpeakStatus = 'idle' | 'loading' | 'unavailable' | 'off';

export interface SpeakerButtonProps {
  readonly text: string;
}

export function SpeakerButton({ text }: SpeakerButtonProps) {
  const { t } = useTranslation('common');
  const token = useProfileStore((s) => s.profile?.token);
  // The EFFECTIVE voice setting: a grown-up's "voices off" lock takes the button away.
  const voiceOn = useAudioSettingsStore((s) => effectiveAudio(s).voiceOn);
  const [status, setStatus] = useState<SpeakStatus>('idle');
  const enabled = token !== undefined && voiceOn;

  const go = async () => {
    if (!enabled) return;
    setStatus('loading');
    const result = await speak(text, { token: token as string });
    setStatus(result === 'ok' ? 'idle' : result === 'off' ? 'off' : 'unavailable');
  };

  // `off` covers both ways nothing can be heard: the voices are locked off, or the device is
  // muted. The button is disabled when the voices are locked off, so in practice it is the mute.
  const label =
    status === 'loading'
      ? t('speaking')
      : status === 'unavailable'
        ? t('try-again')
        : status === 'off'
          ? t('sound-is-off')
          : t('speak');

  return (
    <button
      type="button"
      data-testid="speaker-button"
      aria-label={status === 'loading' ? t('speaking') : t('speak')}
      disabled={!enabled || status === 'loading'}
      onClick={() => void go()}
      className="inline-flex items-center gap-1 rounded-full border-2 border-sun/40 bg-white px-2 py-1 text-xs font-bold text-ink transition-colors hover:bg-sun/30 disabled:opacity-40"
    >
      <Volume2 size={14} />
      {label}
    </button>
  );
}
